import React, { createContext, useContext, useState } from 'react';
import { useAuth0, User } from '@auth0/auth0-react';
import { currentPath, isAuthCallback } from '../lib/authRedirect';

/** The subset of auth methods/state the app actually uses. */
export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user?: User;
  getAccessTokenSilently: () => Promise<string>;
  /** Sign in by redirect and come back to the current page. */
  signIn: () => Promise<void>;
  logout: (opts?: { logoutParams?: { returnTo?: string } }) => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

/** Hook — drop-in replacement for useAuth0(). */
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an <AuthProvider>');
  return ctx;
}

/** Production provider — wraps the real Auth0 SDK. */
export const Auth0BackedProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const auth0 = useAuth0();
  // Read once, at first render: returning from Auth0 with ?code=&state=.
  const [returning] = useState(() => isAuthCallback(window.location.search));
  const value: AuthState = {
    isAuthenticated: auth0.isAuthenticated,
    isLoading: auth0.isLoading,
    user: auth0.user,
    getAccessTokenSilently: auth0.getAccessTokenSilently,
    signIn: () => auth0.loginWithRedirect({ appState: { returnTo: currentPath() } }),
    logout: auth0.logout,
  };
  // Held back until the callback is handled; see lib/authRedirect.ts. The
  // router must not mount and redirect away from the code first.
  if (returning && auth0.isLoading) return null;
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/** Stub provider — no auth gate, used for local dev and E2E tests. */
export const NoAuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const value: AuthState = {
    isAuthenticated: true,
    isLoading: false,
    user: { sub: 'local|karl', name: 'Karl Svensson', email: 'karldanielsvensson@gmail.com' },
    getAccessTokenSilently: () => Promise.resolve('no-auth-token'),
    signIn: () => Promise.resolve(),
    logout: () => Promise.resolve(),
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { AuthContext };
